import { useState, useEffect, useRef } from "react";
import {
  applyStationId,
  readRequestedStationIdFromSearch,
  setUserEntry,
  updateStationId,
} from "../../server/Customer";
import { logout } from "../../utils/authUtils";
import { globalFileIcon } from "../../../axiosInstance";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import Accessibility from "../Accessibility";
import CustomerService from "../customerService/CustomerService";
import PrivateAreaList from "../privateArea/PrivateAreaList";
import { FiArrowRight } from "react-icons/fi";
import {
  Headset,
  Settings,
  LogIn,
  User,
  LogOut as LogOutIcon,
  ShoppingCart,
} from "lucide-react";
import { getStationsFromPGW } from "../../server/Station";
import HeaderSearch from "./HeaderSearch";
import { setClientView } from "../../server/Category";
import { setStatus } from "../../server/Category";
import AnchoredPortal from "../ui/AnchoredPortal";
import { setToggleCart } from "../../server/CartsOpen";
import {
  getCustomerHeaderDisplayName,
  hasPendingEmailVerification,
  isGuestRole,
  promptEmailVerification,
} from "../../utils/emailVerification";
import { canUseCustomerFeatures } from "../../utils/userMode";

const HeaderConnection = ({
  user,
  stationId,
  role,
  setAllProduct,
  basicHeader,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState(false);
  const [openPrivateAreaList, setOpenPrivateAreaList] = useState(false);
  const customerServiceBtnRef = useRef(null);
  const personalAreaBtnRef = useRef(null);
  const [adminStation, setAdminStation] = useState([]);
  const [displayMode, setDisplayMode] = useState(
    sessionStorage.getItem("adminDisplayMode") || "allProduct",
  );

  const exit = () => {
    logout({ dispatch, navigate });
  };

  const check = async (stationId) => {
    sessionStorage.setItem("stationId", stationId);
    dispatch(updateStationId(stationId));
    if (user) {
      user.StationId = stationId;
    }
  };

  useEffect(() => {
    if (role) {
      getStationsFromPGW()
        .then((stations) => {
          setAdminStation(stations.data);
          const fromUrl = readRequestedStationIdFromSearch();
          const savedStationId = fromUrl || sessionStorage.getItem("stationId");
          if (fromUrl) {
            applyStationId(dispatch, fromUrl);
            return;
          }
          if (savedStationId) {
            const exists = stations.data.some(
              (s) => String(s.id) === String(savedStationId),
            );
            if (exists) dispatch(updateStationId(savedStationId));
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }, [role, dispatch]);

  const changeProductDisplay = (value) => {
    sessionStorage.setItem("adminDisplayMode", value);
    setDisplayMode(value);
    switch (value) {
      case "allProduct":
        setAllProduct(true);
        dispatch(setClientView(false));
        dispatch(setStatus(true));
        break;
      case "active":
        setAllProduct(false);
        dispatch(setClientView(false));
        dispatch(setStatus(false));
        break;
      case "customer":
        setAllProduct(false);
        dispatch(setClientView(true));
        dispatch(setStatus(false));
        break;
      default:
        console.warn("ערך לא מוכר:", value);
    }
  };

  // Restore last admin display mode after logout/login (Header stays mounted).
  useEffect(() => {
    if (!role) return;
    changeProductDisplay(
      sessionStorage.getItem("adminDisplayMode") || "allProduct",
    );
    // changeProductDisplay is defined above; effect intentionally keyed on role only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const userState = useSelector((state) => state.user);
  const isClientView = useSelector((state) => state.category.isClientView);
  const cartQty = useSelector((state) =>
    (state.productsInCart?.productsInCart ?? []).reduce(
      (n, p) => n + (Number(p.quantity) || 0),
      0,
    ),
  );
  const customerFeatures = canUseCustomerFeatures(role, isClientView, user);
  const isGuestBrowsing =
    isGuestRole() || (!user && !hasPendingEmailVerification());
  const pendingEmailVerification = hasPendingEmailVerification();
  const showLoginButton = !role && isGuestBrowsing;
  const showCustomerUserMenu =
    !role && !isGuestRole() && (Boolean(user) || pendingEmailVerification);
  // Admin personal area sits by logout, and only in תצוגת לקוח.
  const showAdminUserMenu = Boolean(role) && customerFeatures && !basicHeader;
  const showUserMenu = showCustomerUserMenu || showAdminUserMenu;
  const customerDisplayName =
    getCustomerHeaderDisplayName(user) ||
    (pendingEmailVerification ? "החשבון שלי" : null);

  useEffect(() => {
    if (!showAdminUserMenu) {
      setOpenPrivateAreaList(false);
    }
  }, [showAdminUserMenu]);

  const openEmailVerification = () => {
    setOpenPrivateAreaList(false);
    promptEmailVerification(dispatch, { user: userState });
  };

  const personalAreaMenu = showUserMenu ? (
    <AnchoredPortal
      anchorRef={personalAreaBtnRef}
      open={openPrivateAreaList}
      placement="bottom-end"
      offset={10}
    >
      <div className="private-area-list-menu">
        <PrivateAreaList
          setOpenPrivateAreaList={setOpenPrivateAreaList}
          onLogout={exit}
          logoutOnly={pendingEmailVerification}
          onVerifyEmail={openEmailVerification}
        />
      </div>
    </AnchoredPortal>
  ) : null;

  return (
    <div className="header-connection">
      <div className="back-and-logo">
        <button className="back-button" onClick={() => navigate(-1)}>
          <FiArrowRight />
        </button>
        {!basicHeader && (
          <NavLink to="/" className="site-icon">
            <img
              loading="lazy"
              src={globalFileIcon + "hamazon-site-logo.svg"}
              alt="site"
            />
          </NavLink>
        )}
      </div>
      {!basicHeader ? (
        <>
          <HeaderSearch />
          <div className="header-data">
            <nav className="header-nav-pill" aria-label="ניווט ראשי">
              {customerFeatures && (
                <button
                  type="button"
                  className="mobile-cart-btn"
                  onClick={() => dispatch(setToggleCart(true))}
                  aria-label="עגלת קניות"
                >
                  <ShoppingCart size={22} strokeWidth={2} aria-hidden />
                  {cartQty > 0 && (
                    <span className="mobile-cart-badge">
                      {cartQty > 99 ? "99+" : cartQty}
                    </span>
                  )}
                </button>
              )}
              <button
                type="button"
                className={`nav-pill-btn nav-pill-btn--icon-only${openMenu ? " nav-pill-btn--active" : ""}${role ? " nav-pill-btn--admin-mobile-hidden" : ""}`}
                onClick={() => setOpenMenu(true)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setOpenMenu(false);
                  if (e.key === "Enter" || e.key === " ") setOpenMenu(true);
                }}
                aria-haspopup="dialog"
                aria-expanded={openMenu}
                aria-label="שירות לקוחות"
                ref={customerServiceBtnRef}
              >
                <Headset size={18} strokeWidth={2} aria-hidden />
                <span>שירות לקוחות</span>
              </button>
              <Accessibility />
              {showCustomerUserMenu && (
                <button
                  type="button"
                  onClick={() => setOpenPrivateAreaList(true)}
                  className={`nav-pill-btn${openPrivateAreaList ? " nav-pill-btn--active" : ""}`}
                  aria-label={customerDisplayName || "אזור אישי"}
                  ref={personalAreaBtnRef}
                >
                  <User size={18} strokeWidth={2} aria-hidden />
                  <span>{customerDisplayName || "אזור אישי"}</span>
                </button>
              )}
              {showLoginButton && (
                <button
                  type="button"
                  className="nav-pill-btn"
                  onClick={() => dispatch(setUserEntry([true, "login"]))}
                  aria-label="כניסה"
                >
                  <LogIn size={18} strokeWidth={2} aria-hidden />
                  <span>כניסה</span>
                </button>
              )}
            </nav>
            <AnchoredPortal
              anchorRef={customerServiceBtnRef}
              open={openMenu}
              placement="bottom-end"
              offset={10}
            >
              <div className="customer-service-menu">
                <CustomerService setOpenMenu={setOpenMenu} />
              </div>
            </AnchoredPortal>
            {showCustomerUserMenu ? personalAreaMenu : null}
          </div>
        </>
      ) : (
        <h2>פאנל הניהול</h2>
      )}

      <div
        className={`login-or-sign-in${role ? " login-or-sign-in--admin" : ""}`}
      >
        {role ? (
          <nav
            className="header-nav-pill header-nav-pill--admin"
            aria-label="פעולות מנהל"
          >
            {basicHeader && (
              <NavLink
                to="/"
                className="nav-pill-btn nav-pill-btn--icon-only"
                aria-label="קטלוג"
              >
                <Settings size={18} strokeWidth={2} aria-hidden />
                <span>קטלוג</span>
              </NavLink>
            )}
            {!basicHeader && (
              <NavLink
                to="/admin/users"
                className="nav-pill-btn nav-pill-btn--icon-only nav-pill-btn--admin-mobile-hidden"
                aria-label="פאנל הניהול"
              >
                <Settings size={18} strokeWidth={2} aria-hidden />
                <span>פאנל הניהול</span>
              </NavLink>
            )}
            {!basicHeader && (
              <label className="nav-pill-btn nav-pill-btn--select">
                <select
                  className="nav-pill-btn__select"
                  value={displayMode}
                  onChange={(e) => changeProductDisplay(e.target.value)}
                >
                  <option value="allProduct">תצוגת כל המוצרים</option>
                  <option value="active">תצוגת פעילים</option>
                  <option value="customer">תצוגת לקוח</option>
                </select>
              </label>
            )}
            <label className="nav-pill-btn nav-pill-btn--select">
              <span>מנהל /</span>
              <select
                className="nav-pill-btn__select"
                value={stationId || ""}
                onChange={(e) => check(e.target.value)}
              >
                {adminStation.map((station) => (
                  <option key={station.id} value={station.id}>
                    {station.name}
                  </option>
                ))}
              </select>
            </label>
            {showAdminUserMenu && (
              <button
                type="button"
                onClick={() => setOpenPrivateAreaList(true)}
                className={`nav-pill-btn${openPrivateAreaList ? " nav-pill-btn--active" : ""}`}
                aria-label={customerDisplayName || "אזור אישי"}
                ref={personalAreaBtnRef}
              >
                <User size={18} strokeWidth={2} aria-hidden />
                <span>{customerDisplayName || "אזור אישי"}</span>
              </button>
            )}
            {!showAdminUserMenu && (
              <button
                type="button"
                onClick={() => exit()}
                className="nav-pill-btn nav-pill-btn--admin-mobile-hidden"
                aria-label="יציאה"
              >
                <LogOutIcon size={18} strokeWidth={2} aria-hidden />
                <span>יציאה</span>
              </button>
            )}
            {showAdminUserMenu ? personalAreaMenu : null}
          </nav>
        ) : null}
      </div>
    </div>
  );
};

export default HeaderConnection;
